import { DraftFiling } from 'lib-knowledge-management-common/components/draftFiling';
import { useMemo } from 'react';

import { useCollections } from '../hooks/useCollections';
import { UseSuggestionActions } from '../hooks/useSuggestionActions';
import { Suggestion } from '../utils/toSuggestion';

type SuggestionFilingProps = {
  suggestion: Suggestion;
  actions: UseSuggestionActions;
};

/**
 * Where the draft would be filed, as the panel behind the breadcrumb's pencil.
 *
 * The panel itself is shared with the agent interface's AI draft modal; this
 * only wires it to the suggestion's own re-file action.
 */
export function SuggestionFiling({
  suggestion,
  actions,
}: SuggestionFilingProps): JSX.Element {
  const { collections, loading, hasMore, fetchNextPage } = useCollections();
  const { possibleCollectionIds } = suggestion;

  // The backend refuses a collection the draft's queue is not linked to, so the
  // picker offers only those. An empty set is not "nowhere" but "anywhere": it
  // means the queue names no collections of its own, or the draft records no
  // queue at all.
  const filingOptions = useMemo(
    () =>
      possibleCollectionIds.length === 0
        ? collections
        : collections.filter((collection) =>
            possibleCollectionIds.includes(collection.id),
          ),
    [collections, possibleCollectionIds],
  );

  return (
    <DraftFiling
      collections={filingOptions}
      collectionsLoading={loading}
      hasMoreCollections={hasMore}
      onLoadMoreCollections={fetchNextPage}
      collectionId={suggestion.collectionId}
      categoryId={suggestion.categoryId}
      onChange={actions.refile}
      isSaving={actions.isSaving || actions.isRefiling}
    />
  );
}
